/**
 * Execute logic for the "New Connection from Clipboard" command-palette action.
 *
 * Reads the clipboard on demand, parses it, fires the paste analytics event
 * (source = command_palette), hands the parse to the New Connection dialog and
 * opens it. The dialog never opens empty (Option C C3, ruling after review r6):
 * a string with an engine prefills that engine's form; one that asks which
 * engine ("Reads as") or one the rules refused goes into the start view's
 * filter, where the card asks or says why; anything else only raises the
 * start view's paste hint — that text is never handed over.
 *
 * Extracted from the command definition so it can be unit-tested with mocked
 * clipboard + stores.
 */

import { parseConnectionString } from './parseConnectionString';
import { clipboardConnectionText, readClipboardText } from './clipboardPreview';
import { useConnectionStringPrefillStore } from '../../store/connectionStringPrefillStore';
import { useConnectionsUIStore } from '../../store/uiStore';

export async function executeNewConnectionFromClipboard(): Promise<void> {
  let raw = '';
  try {
    raw = await readClipboardText();
  } catch {
    raw = '';
  }

  // Line breaks are dropped the way the start filter drops them from a paste, so both parse the same text.
  const text = clipboardConnectionText(raw);
  const result = parseConnectionString(text);

  // Analytics: low-cardinality only — never the raw string, host, or password.
  try {
    void window.electronAPI?.analytics
      ?.track('feature:connection_string_paste', {
        engine: result.engine ?? 'none',
        // Several engines read it alike: it asks which (not a failure); the palette opens the dialog empty for it.
        outcome: result.choices ? 'choice' : result.outcome,
        format: result.format,
        source: 'command_palette',
        // The palette hands its parse to the New Connection dialog (funnel v2, Option C C3).
        funnel_version: 2,
      })
      ?.catch?.(() => {});
  } catch {
    // Analytics must never break the flow.
  }

  const forStartFilter = !result.engine && (result.choices !== undefined || result.refusal !== undefined);
  const handoffId = useConnectionStringPrefillStore.getState().begin({ result, input: result.engine || forStartFilter ? text : '' });
  useConnectionsUIStore.getState().setShowNewConnectionDialog(true, handoffId);
}
